import { APP_KEY } from "../../StorageKeys";

import * as Utils from "./Utils";
import * as StorageUtils from "./StorageUtils";

const ON = "on";
const OFF = "off";

export interface AppInfo {
	ci: boolean;
	environment: string;
	appEnv: string;
	version: string;
	build?: {
		started: number;
		commit: string;
		commitMessage: string;
		buildId: string;
	};
}

// called Utils.once and cached
export const getAppInfo = Utils.once(() => {
	/**
	 * For some bizzare reason if we try getEnvironmentVariable('APP') it comes back as undefined but
	 * if we access with process.env.APP it is not. Comes down to something webpack related probably
	 * since process.env[key] where key is 'APP' returns undefined where as process.env['APP'] does not.
	 * Aggressive tree shaking thanks a lot.
	 */

	const includeAppEnv = getNodeEnv() === "development";
	return {
		...((process.env.APP as any) || { version: "unknown" }),
		...(includeAppEnv && { appEnv: getAppEnvironment() }),
		environment: getEnvironmentVariable("NODE_ENV") || "unknown",
	} as AppInfo;
});

export const getEnvironmentVariable = Utils.memoize(
	(key: string, defaultValue?: string): string | undefined => {
		return process.env[key] || defaultValue;
	},
);

export const isEnvironmentVariable = Utils.memoize((key: string) => {
	return getEnvironmentVariable(key, OFF) === ON;
});

export const getApiKey = Utils.once(() => {
	const apiKey = getEnvironmentVariable("REACT_APP_API_KEY");
	if (!apiKey) {
		throw new Error("No REACT_APP_API_KEY env var specified");
	}
	return apiKey;
});

export const getAppEnvironmentDisplayName = Utils.memoize(
	(includeDev = true) => {
		let env = getAppEnvironment();
		if (env === "prod") {
			env = "production";
		}
		if (!isProductionBuild() && includeDev) {
			env += " (dev)";
		}
		return env;
	},
);

export const getAppLinksBaseUrl = () => {
	const env = getAppEnvironment();
	switch (env) {
		case "staging":
			return "https://api-stage.brightmile.io/links";
		case "qa":
		case "local":
			return "https://api-qa.brightmile.io/links";
		default:
			return "https://api.brightmile.io/links";
	}
};

export const getEnvironmentInternalName = (
	includeLocal = false,
	prefix = "zenefleet",
) => {
	const env = getAppEnvironment();
	let name = "qa";
	if (env === "staging") {
		name = "stage";
	} else if (env === "prod") {
		name = "";
	} else if (env === "local" && includeLocal) {
		name = "local";
	}
	return `${prefix}${name ? "-" + name : ""}`;
};

export const getBrokerBaseUrl = () => {
	const env = getAppEnvironment();
	switch (env) {
		case "staging":
			return "https://broker-stage.5keysengage.com";
		case "qa":
		case "local":
			return "https://broker-qa.5keysengage.com";
		default:
			return "https://broker.5keysengage.com";
	}
};

export const getPinionBaseUrl = () => {
	const env = getAppEnvironment();
	switch (env) {
		case "staging":
			return "https://test.5keysengage.com";
		case "qa":
		case "local":
			return "https://dev.5keysengage.com";
		default:
			return "https://5keysengage.com";
	}
};

export const getNodeEnv = Utils.once(() => {
	return getEnvironmentVariable("NODE_ENV");
});

export const isProductionBuild = Utils.once(() => {
	return getNodeEnv() === "production";
});

export const isProduction = Utils.once(() => {
	return getAppEnvironment() === "prod" && isProductionBuild();
});

export const getAppEnvironment = Utils.once(() => {
	const env = getEnvironmentVariable("REACT_APP_ENV");
	if (!env) {
		throw new Error("No REACT_APP_ENV env var specified");
	}
	return env;
});

export const isTest = Utils.once(() => {
	return getNodeEnv() === "test";
});

export const getProjectId = Utils.once(() => {
	return getEnvironmentVariable("REACT_APP_PROJECT_ID");
});

export const getBaseUrl = Utils.once(() => {
	let baseUrl = getEnvironmentVariable("REACT_APP_API_BASE_URL");
	if (!baseUrl) {
		throw new Error("No REACT_APP_API_BASE_URL env var specified");
	}
	baseUrl = baseUrl.trim();
	if (baseUrl.endsWith("/")) {
		baseUrl = baseUrl.substr(0, baseUrl.length - 1);
	}
	const projectId = getProjectId();
	if (projectId) {
		baseUrl = baseUrl.replace(/{projectId}/g, projectId);
	}
	return baseUrl;
});

export const refreshVersion = () => {
	StorageUtils.set(APP_KEY, getAppInfo());
};

export const hasAppEnvChanged = () => {
	let envChanged = false;
	const currentEnv = getNodeEnv();

	if (currentEnv === "development") {
		const currentAppEnv = getAppEnvironment();
		const prevApp = StorageUtils.get(APP_KEY);
		const prevEnv = (prevApp as any)?.appEnv;
		envChanged = prevEnv && prevEnv !== currentAppEnv;
	}

	return envChanged;
};
